import type { ReactNode } from "react"
import { createContext, useContext } from "react"
import { useTranslation } from "react-i18next"
import { z } from "zod"
import { fspConfig } from "../init/fsp-config"
import type { TI18nLang } from "./types"

const I18N_LANG_OPTIONS = ["en-GB", "is", "da"] as const satisfies ReadonlyArray<TI18nLang>
export const i18nLangSchema = z.enum(I18N_LANG_OPTIONS)

const LangContext = createContext<TI18nLang>("en-GB")

export const LangProvider = ({ children }: { children: ReactNode }) => {
  const func = "LangProvider"
  console.log(`%c${func}`, "color:CornflowerBlue")
  const { ns } = fspConfig
  console.log("🌐", { func }, { ns })

  // The useTranslation hook also returns a 'ready' boolean flag that indicates whether translations have been fetched or not.
  // As we fetch translations in the root loader (and LangProvider lives sufficiently far down the tree)
  // translations will always have been fetched at this point and so we don't need to use the 'ready' flag.
  // const { t, i18n } = useTranslation(ns, { useSuspense: false })

  const { t, i18n } = useTranslation(ns, { useSuspense: false })

  // We need to be able to access the language directly to
  // 1. use the correct language in API responses that give data in multiple languages (like the menu)
  // i18next can report languages we don't support (e.g. from a browser detector),
  // so parse instead of trusting the value, and fall back to the default language.
  const langResult = i18nLangSchema.safeParse(i18n.language)
  if (!langResult.success) {
    console.warn("🌐 Unsupported i18next language, falling back to 'en-GB'", {
      func,
      language: i18n.language,
    })
  }
  const lang: TI18nLang = langResult.success ? langResult.data : "en-GB"
  console.log("🌐 Language:", lang)
  console.log("🌐 Translations:", {
    title: t("helmet.title"),
    meta: t("helmet.meta"),
  })

  console.log("🌐❗ Translations loaded!")

  return <LangContext value={lang}>{children}</LangContext>
}

export const useLang = () => {
  const func = "useLang"
  console.log({ func })
  const context = useContext(LangContext)
  return context
}
